import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MinusIcon, PlusIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import './AvailabilityEditor.css';

interface AvailabilityEditorProps {
  daysPerWeek: number;
  onChange: (daysPerWeek: number) => void;
}

const AvailabilityEditor: React.FC<AvailabilityEditorProps> = ({ daysPerWeek, onChange }) => {
  const { t } = useTranslation();
  const [pendingValue, setPendingValue] = useState(daysPerWeek ?? 0);
  const [hasChanges, setHasChanges] = useState(false);

  // Update pending value when prop changes (from external save)
  useEffect(() => {
    setPendingValue(daysPerWeek ?? 0);
    setHasChanges(false);
  }, [daysPerWeek]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    // Allow values between 0 and 5 with one decimal place
    if (!isNaN(value) && value >= 0 && value <= 5) {
      // Round to 1 decimal place
      const rounded = Math.round(value * 10) / 10;
      setPendingValue(rounded);
      setHasChanges(rounded !== daysPerWeek);
    } else if (e.target.value === '') {
      setPendingValue(0);
      setHasChanges(0 !== daysPerWeek);
    }
  };

  const handleIncrement = () => {
    const newValue = Math.min(5, Math.round((pendingValue + 0.5) * 10) / 10);
    setPendingValue(newValue);
    setHasChanges(newValue !== daysPerWeek);
  };

  const handleDecrement = () => {
    const newValue = Math.max(0, Math.round((pendingValue - 0.5) * 10) / 10);
    setPendingValue(newValue);
    setHasChanges(newValue !== daysPerWeek);
  };

  const handleApply = () => {
    onChange(pendingValue);
    setHasChanges(false);
  };

  const handleCancel = () => {
    setPendingValue(daysPerWeek);
    setHasChanges(false);
  };

  return (
    <div className="availability-editor">
      <div className="availability-editor-controls">
        <button
          type="button"
          onClick={handleDecrement}
          disabled={pendingValue <= 0}
          className="availability-editor-btn availability-editor-btn--decrement"
          aria-label={t('admin.userManagement.responsibilities.decrease', 'Diminuer')}
        >
          <MinusIcon className="availability-editor-btn-icon" />
        </button>

        <div className="availability-editor-input-wrapper">
          <input
            type="number"
            min="0"
            max="5"
            step="0.5"
            value={pendingValue}
            onChange={handleChange}
            className="availability-editor-input"
          />
          <span className="availability-editor-suffix">
            {t('admin.userManagement.responsibilities.daysPerWeek', 'j/sem')}
          </span>
        </div>

        <button
          type="button"
          onClick={handleIncrement}
          disabled={pendingValue >= 5}
          className="availability-editor-btn availability-editor-btn--increment"
          aria-label={t('admin.userManagement.responsibilities.increase', 'Augmenter')}
        >
          <PlusIcon className="availability-editor-btn-icon" />
        </button>

        {hasChanges && (
          <>
            <button
              type="button"
              onClick={handleCancel}
              className="availability-editor-btn availability-editor-btn--cancel"
              aria-label={t('common.cancel', 'Annuler')}
            >
              <XMarkIcon className="availability-editor-btn-icon" />
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="availability-editor-btn availability-editor-btn--apply"
              aria-label={t('common.apply', 'Appliquer')}
            >
              <CheckIcon className="availability-editor-btn-icon" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export { AvailabilityEditor };
export default AvailabilityEditor;
